import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Link2, Plus, X, PackageSearch } from "lucide-react";

const BASE_UNITS = [{ v: "g", l: "grammi (g)" }, { v: "ml", l: "millilitri (ml)" }, { v: "pz", l: "pezzi (pz)" }];
const money = (n, dp = 5) => (n == null ? "—" : `€ ${Number(n).toFixed(dp)}`);

export default function FoodCostPending() {
  const [rows, setRows] = useState([]);
  const [ings, setIngs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);          // line being associated
  const [mode, setMode] = useState("existing");  // existing | new
  const [form, setForm] = useState({});
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, i] = await Promise.all([api.get("/fc/lines/pending"), api.get("/fc/ingredients?active=true")]);
      setRows(p.data || []); setIngs(i.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAssoc = (ln) => {
    setSel(ln); setMode("existing"); setPreview(null);
    setForm({
      ingredient_id: "", canonical_name: ln.description || "", category: "", base_unit: "ml",
      quantity: ln.quantity, pack_size: ln.pack_size || 1, purchase_unit: ln.purchase_unit || "",
      content_value: "", content_unit: "",
    });
  };

  const doPreview = async () => {
    if (!sel) return;
    const base_unit = mode === "existing" ? (ings.find((i) => i.id === form.ingredient_id)?.base_unit) : form.base_unit;
    if (!base_unit) return toast.error("Seleziona un ingrediente / unità base");
    try {
      const { data } = await api.post(`/fc/lines/${sel.id}/preview`, {
        base_unit, quantity: Number(form.quantity) || 0, pack_size: Number(form.pack_size) || 1,
        purchase_unit: form.purchase_unit,
        content_value: form.content_value === "" ? null : Number(form.content_value),
        content_unit: form.content_unit || null,
      });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirm = async () => {
    if (!sel) return;
    try {
      const body = {
        quantity: Number(form.quantity) || 0, pack_size: Number(form.pack_size) || 1,
        purchase_unit: form.purchase_unit,
        content_value: form.content_value === "" ? null : Number(form.content_value),
        content_unit: form.content_unit || null,
      };
      let res;
      if (mode === "existing") {
        if (!form.ingredient_id) return toast.error("Seleziona un ingrediente");
        res = await api.post(`/fc/lines/${sel.id}/associate`, { ingredient_id: form.ingredient_id, confirm_alias: true, ...body });
      } else {
        if (!form.canonical_name.trim()) return toast.error("Nome ingrediente obbligatorio");
        res = await api.post(`/fc/lines/${sel.id}/create-ingredient`, {
          canonical_name: form.canonical_name, category: form.category, base_unit: form.base_unit, ...body });
      }
      toast.success(`Associata${res.data.auto_associated ? ` · +${res.data.auto_associated} righe simili` : ""}`);
      setSel(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ignore = async (ln) => { try { await api.post(`/fc/lines/${ln.id}/ignore`); toast.success("Riga ignorata"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const baseUnit = mode === "existing" ? (ings.find((i) => i.id === form.ingredient_id)?.base_unit) : form.base_unit;

  return (
    <div data-testid="fc-pending-page">
      <PageHeader title="Food Cost · Prodotti da associare" subtitle="Collega le righe fattura a un ingrediente master" testid="fc-pending-header" />

      <Card className="p-5 rounded-xl" data-testid="fc-pending-card">
        {loading ? (
          <p className="text-muted-foreground py-8 text-center">Caricamento…</p>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground" data-testid="fc-pending-empty">
            <PackageSearch className="w-10 h-10 mx-auto mb-3 text-primary/60" />
            Nessun prodotto da associare. Tutte le righe fattura sono collegate.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2 px-2">Descrizione</th><th className="py-2 px-2 text-right">Q.tà</th>
                <th className="py-2 px-2">Unità</th><th className="py-2 px-2 text-right">Netto</th>
                <th className="py-2 px-2">P.IVA</th><th className="py-2 px-2">Data</th><th className="py-2 px-2 text-right">Azioni</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0" data-testid={`fc-pending-row-${r.id}`}>
                    <td className="py-2 px-2 font-medium">{r.description}</td>
                    <td className="py-2 px-2 text-right">{r.quantity}</td>
                    <td className="py-2 px-2">{r.purchase_unit || "—"}</td>
                    <td className="py-2 px-2 text-right">€ {(Number(r.line_net_total) || 0).toFixed(2)}</td>
                    <td className="py-2 px-2 text-muted-foreground">{r.supplier_vat || "—"}</td>
                    <td className="py-2 px-2 text-muted-foreground">{r.doc_date || "—"}</td>
                    <td className="py-2 px-2 text-right whitespace-nowrap">
                      <Button size="sm" className="rounded-full h-8" onClick={() => openAssoc(r)} data-testid={`fc-associate-${r.id}`}><Link2 className="w-3.5 h-3.5 mr-1" />Associa</Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 ml-1" onClick={() => ignore(r)} data-testid={`fc-ignore-${r.id}`}><X className="w-4 h-4 text-muted-foreground" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg" data-testid="fc-assoc-dialog">
          <DialogHeader><DialogTitle>Associa: {sel?.description}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button size="sm" variant={mode === "existing" ? "default" : "outline"} className="rounded-full" onClick={() => { setMode("existing"); setPreview(null); }} data-testid="fc-mode-existing">Ingrediente esistente</Button>
              <Button size="sm" variant={mode === "new" ? "default" : "outline"} className="rounded-full" onClick={() => { setMode("new"); setPreview(null); }} data-testid="fc-mode-new"><Plus className="w-3.5 h-3.5 mr-1" />Nuovo ingrediente</Button>
            </div>

            {mode === "existing" ? (
              <div className="space-y-1.5">
                <Label>Ingrediente master</Label>
                <Select value={form.ingredient_id} onValueChange={(v) => { setForm({ ...form, ingredient_id: v }); setPreview(null); }}>
                  <SelectTrigger data-testid="fc-assoc-ing-select"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>{ings.map((i) => <SelectItem key={i.id} value={i.id} data-testid={`fc-assoc-ing-${i.id}`}>{i.canonical_name} (/{i.base_unit})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2"><Label>Nome ingrediente</Label><Input value={form.canonical_name} onChange={(e) => setForm({ ...form, canonical_name: e.target.value })} data-testid="fc-new-name" /></div>
                <div className="space-y-1.5"><Label>Categoria</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="fc-new-category" /></div>
                <div className="space-y-1.5"><Label>Unità base</Label>
                  <Select value={form.base_unit} onValueChange={(v) => { setForm({ ...form, base_unit: v }); setPreview(null); }}>
                    <SelectTrigger data-testid="fc-new-baseunit"><SelectValue /></SelectTrigger>
                    <SelectContent>{BASE_UNITS.map((u) => <SelectItem key={u.v} value={u.v}>{u.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="rounded-lg bg-secondary/50 p-3 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground">Correggi confezione / quantità</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1"><Label className="text-xs">Q.tà acquistata</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="fc-corr-qty" /></div>
                <div className="space-y-1"><Label className="text-xs">Unità acquisto</Label><Input value={form.purchase_unit} onChange={(e) => setForm({ ...form, purchase_unit: e.target.value })} data-testid="fc-corr-unit" /></div>
                <div className="space-y-1"><Label className="text-xs">Confezione (×)</Label><Input type="number" value={form.pack_size} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} data-testid="fc-corr-pack" /></div>
                <div className="space-y-1"><Label className="text-xs">Contenuto</Label><Input type="number" value={form.content_value} placeholder="es. 1" onChange={(e) => setForm({ ...form, content_value: e.target.value })} data-testid="fc-corr-content" /></div>
                <div className="space-y-1"><Label className="text-xs">Unità cont.</Label><Input value={form.content_unit} placeholder="es. l / ml / g" onChange={(e) => setForm({ ...form, content_unit: e.target.value })} data-testid="fc-corr-content-unit" /></div>
              </div>
              <Button size="sm" variant="outline" className="rounded-full w-full" onClick={doPreview} disabled={!baseUnit} data-testid="fc-preview-btn">Anteprima costo</Button>
              {preview && (
                <div className="text-sm rounded-md bg-background p-2 border border-border" data-testid="fc-preview-result">
                  <p>Quantità base: <b>{preview.base_quantity} {preview.base_unit}</b></p>
                  <p>Costo: <b>{money(preview.cost_per_base_unit)}/{preview.base_unit}</b></p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setSel(null)}>Annulla</Button>
            <Button className="rounded-full" onClick={confirm} data-testid="fc-confirm-assoc">Conferma associazione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
